/**
 * DropdownPortal — escapes all parent stacking contexts via React DOM portal.
 *
 * Use with useDropdownAnchor() to anchor the menu beneath a trigger.
 *
 *   const { ref, pos, measure } = useDropdownAnchor();
 *
 *   <View ref={ref as any} collapsable={false}>
 *     <TouchableOpacity onPress={() => { measure(); setOpen(!open); }}>...</TouchableOpacity>
 *   </View>
 *
 *   <DropdownPortal open={open} onClose={() => setOpen(false)} pos={pos}
 *     backgroundColor={colors.card} borderColor={colors.cardBorder}>
 *     {menuContent}
 *   </DropdownPortal>
 */
import React, { useCallback, useRef, useState, ReactNode } from 'react';
import { View, Platform, Modal, Pressable, ViewStyle } from 'react-native';
import { createPortal } from 'react-dom';

import { tokens } from '@design';

export interface DropdownPos {
  top: number;
  left: number;
  width: number;
}

export function useDropdownAnchor(minWidth: number = 220) {
  const ref = useRef<View | null>(null);
  const [pos, setPos] = useState<DropdownPos>({ top: 0, left: 0, width: minWidth });

  const measure = useCallback(() => {
    if (!ref.current) return;
    if (Platform.OS === 'web') {
      const rect = (ref.current as unknown as HTMLElement).getBoundingClientRect?.();
      if (rect) {
        setPos({
          top: rect.bottom + 4,
          left: rect.left,
          width: Math.max(rect.width, minWidth),
        });
      }
    } else if ((ref.current as any).measureInWindow) {
      (ref.current as any).measureInWindow(
        (x: number, y: number, w: number, h: number) => {
          setPos({ top: y + h + 4, left: x, width: Math.max(w, minWidth) });
        },
      );
    }
  }, [minWidth]);

  return { ref, pos, measure };
}

interface DropdownPortalProps {
  open: boolean;
  onClose: () => void;
  pos: DropdownPos;
  backgroundColor: string;
  borderColor: string;
  maxHeight?: number;
  modalStyle?: ViewStyle;
  children: ReactNode;
}

export function DropdownPortal({
  open,
  onClose,
  pos,
  backgroundColor,
  borderColor,
  maxHeight = 320,
  modalStyle,
  children,
}: DropdownPortalProps) {
  if (!open) return null;

  if (Platform.OS === 'web') {
    return createPortal(
      <>
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9998,
          }}
          onClick={onClose}
        />
        <div
          style={{
            position: 'fixed',
            top: pos.top,
            insetInlineStart: pos.left,
            width: pos.width,
            maxHeight,
            zIndex: 9999,
            backgroundColor,
            borderRadius: tokens.radii.absolute['radius-8'],
            border: `1px solid ${borderColor}`,
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
            overflow: 'auto',
          }}
        >
          {children}
        </div>
      </>,
      document.body,
    );
  }

  return (
    <Modal transparent visible animationType="fade" onRequestClose={onClose}>
      <Pressable
        style={{
          flex: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 16,
        }}
        onPress={onClose}
      >
        <Pressable
          style={[
            {
              backgroundColor,
              borderColor,
              borderWidth: 1,
              borderRadius: tokens.radii.absolute['radius-8'],
              width: '100%',
              maxWidth: 360,
              maxHeight,
              overflow: 'hidden',
            },
            modalStyle,
          ]}
          onPress={(e) => e.stopPropagation?.()}
        >
          {children}
        </Pressable>
      </Pressable>
    </Modal>
  );
}

export default DropdownPortal;
